import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { EmployeesService } from '../shared/services/employees.service';
import { Employee, EmployeesResponse } from '../shared/models/employee';
import { AssignedCourseItem, AssignedCoursesResponse, Course, CourseResponse } from '../shared/models/course';
import { CoursesService } from '../shared/services/courses.service';
import { BulkAssignDialogComponent } from '../shared/components/bulk-assign-dialog/bulk-assign-dialog.component';

@Component({
  selector: 'app-employees',
  standalone: true,
  imports: [CommonModule, BulkAssignDialogComponent],
  templateUrl: './employees.component.html',
  styleUrls: ['./employees.component.scss']
})
export class EmployeesComponent implements OnInit {
  readonly loading = signal<boolean>(false);
  readonly errorText = signal<string>('');
  private errorTimer: any = null;

  // Teams and members
  readonly teams = signal<Array<{ name: string; count: number; members: Employee[] }>>([]);

  // Expanded state
  readonly expandedTeams = signal<Set<string>>(new Set());
  readonly expandedMembers = signal<Map<string, Set<string>>>(new Map());

  // Assigned courses cache per member
  readonly assignedMap = signal<Map<string, { loading: boolean; courses?: AssignedCourseItem[]; error?: string }>>(new Map());

  // Course names cache (courseId -> courseName)
  readonly courseNameMap = signal<Map<string, string>>(new Map());

  // Assign Course dialog state
  readonly assignOpen = signal<boolean>(false);
  readonly assignMember = signal<string | null>(null);
  readonly assignLoading = signal<boolean>(false);
  readonly assignError = signal<string>('');
  private assignErrorTimer: any = null;
  readonly availableCourses = signal<Course[]>([]);
  readonly selectedCourseId = signal<string>('');

  // Bulk assign state
  readonly bulkAssignOpen = signal<boolean>(false);
  readonly bulkAssignMembers = signal<Array<{id: string, name: string}>>([]);
  readonly bulkAssignCourses = signal<Course[]>([]);
  readonly bulkAssignCoursesLoading = signal<boolean>(false);

  constructor(
    private readonly employees: EmployeesService,
    private readonly courses: CoursesService
  ) {}

  ngOnInit(): void {
    this.fetchEmployees();
  }

  private fetchEmployees(): void {
    this.loading.set(true);
    this.errorText.set('');
    this.employees.getEmployees().subscribe({
      next: (res: EmployeesResponse) => {
        const groups = res?.body?.TEAMS ?? {};
        const arr: Array<{ name: string; count: number; members: Employee[] }> = [];
        for (const [teamName, group] of Object.entries(groups)) {
          const members = Array.isArray(group?.TEAM_MEMBERS) ? group.TEAM_MEMBERS : [];
          arr.push({ name: teamName, count: group?.COUNT ?? members.length, members });
        }
        arr.sort((a, b) => a.name.localeCompare(b.name));
        this.teams.set(arr);
        this.loading.set(false);
      },
      error: (err) => {
        this.setError(this.describeError(err));
        this.loading.set(false);
      }
    });
  }

  toggleTeam(team: string): void {
    const set = new Set(this.expandedTeams());
    if (set.has(team)) set.delete(team);
    else set.add(team);
    this.expandedTeams.set(set);
  }

  isTeamOpen(team: string): boolean {
    return this.expandedTeams().has(team);
  }

  toggleMember(team: string, memberEmail: string | null | undefined): void {
    if (!memberEmail) return;
    const map = new Map(this.expandedMembers());
    const set = new Set(map.get(team) ?? new Set<string>());
    if (set.has(memberEmail)) {
      set.delete(memberEmail);
    } else {
      set.add(memberEmail);
      // fetch assigned if not cached
      if (!this.assignedMap().has(memberEmail)) {
        const next = new Map(this.assignedMap());
        next.set(memberEmail, { loading: true });
        this.assignedMap.set(next);
        this.employees.getAssignedFor(memberEmail).subscribe({
          next: (resp: AssignedCoursesResponse) => {
            const body: any[] = Array.isArray((resp as any)?.body) ? (resp as any).body : [];
            let courses: AssignedCourseItem[] = [];
            if (body.length > 0 && Array.isArray((body[0] as any)?.COURSES)) {
              // Old shape
              courses = body.flatMap((it: any) => Array.isArray(it?.COURSES) ? it.COURSES : []) as AssignedCourseItem[];
            } else {
              // New shape (flat rows)
              courses = body
                .filter((row: any) => row && row.COURSE_ID && row.STATUS)
                .map((row: any) => ({
                  COURSE_ID: String(row.COURSE_ID),
                  STATUS: String(row.STATUS).toUpperCase() as AssignedCourseItem['STATUS'],
                  ASSIGNED_ON: row.ASSIGNED_ON ?? undefined,
                  STARTED_ON: row.STARTED_ON ?? undefined,
                  COMPLETED_ON: row.COMPLETED_ON ?? undefined
                }));
            }
            const updated = new Map(this.assignedMap());
            updated.set(memberEmail, { loading: false, courses });
            this.assignedMap.set(updated);

            // Prefetch course names for the set of course IDs not yet cached
            this.prefetchCourseNames(courses);
          },
          error: (err) => {
            const updated = new Map(this.assignedMap());
            updated.set(memberEmail, { loading: false, error: this.describeError(err) });
            this.assignedMap.set(updated);
          }
        });
      }
    }
    map.set(team, set);
    this.expandedMembers.set(map);
  }

  isMemberOpen(team: string, memberEmail: string | null | undefined): boolean {
    if (!memberEmail) return false;
    return this.expandedMembers().get(team)?.has(memberEmail) ?? false;
  }

  assignedFor(memberEmail: string | null | undefined) {
    if (!memberEmail) return undefined;
    return this.assignedMap().get(memberEmail);
  }

  getCourseName(courseId?: string | null): string {
    if (!courseId) return '—';
    return this.courseNameMap().get(courseId) ?? '—';
  }

  badgeClass(status: string): string {
    const s = (status || '').toUpperCase();
    if (s === 'COMPLETED') return 'badge badge--green';
    if (s === 'IN_PROGRESS') return 'badge badge--orange';
    if (s === 'ASSIGNED') return 'badge badge--blue';
    return 'badge';
  }

  private prefetchCourseNames(items: AssignedCourseItem[]): void {
    const needIds = Array.from(
      new Set(
        (items || [])
          .map(i => i.COURSE_ID)
          .filter(id => !!id && !this.courseNameMap().has(id as string)) as string[]
      )
    );
    if (needIds.length === 0) return;

    const requests = needIds.map(id =>
      this.courses.getCourse(id).pipe(
        map((res: CourseResponse) => ({ id, name: res?.body?.COURSE_NAME ?? id })),
        catchError(() => of({ id, name: id }))
      )
    );

    forkJoin(requests).subscribe((pairs) => {
      const m = new Map(this.courseNameMap());
      for (const p of pairs) {
        m.set(p.id, p.name);
      }
      this.courseNameMap.set(m);
    });
  }

  // Assign Course dialog handlers
  openAssignDialog(memberEmail: string | null | undefined): void {
    if (!memberEmail) return;
    this.assignOpen.set(true);
    this.assignMember.set(memberEmail);
    this.assignLoading.set(true);
    this.assignError.set('');

    // Ensure we have current assigned list for the member
    const maybe = this.assignedMap().get(memberEmail)?.courses;
    const assigned$ = maybe
      ? of(maybe)
      : this.employees.getAssignedFor(memberEmail).pipe(
          map((resp: AssignedCoursesResponse) => {
            const body: any[] = Array.isArray((resp as any)?.body) ? (resp as any).body : [];
            if (body.length > 0 && Array.isArray((body[0] as any)?.COURSES)) {
              return body.flatMap((it: any) => Array.isArray(it?.COURSES) ? it.COURSES : []) as AssignedCourseItem[];
            }
            return body
              .filter((row: any) => row && row.COURSE_ID && row.STATUS)
              .map((row: any) => ({
                COURSE_ID: String(row.COURSE_ID),
                STATUS: String(row.STATUS).toUpperCase() as AssignedCourseItem['STATUS'],
                ASSIGNED_ON: row.ASSIGNED_ON ?? undefined,
                STARTED_ON: row.STARTED_ON ?? undefined,
                COMPLETED_ON: row.COMPLETED_ON ?? undefined
              }));
          })
        );

    forkJoin([assigned$, this.courses.listCourses()]).subscribe({
      next: ([assigned, listRes]) => {
        const assignedIds = new Set((assigned || []).map(a => a.COURSE_ID));
        const allCourses: Course[] = Array.isArray(listRes?.body) ? listRes.body : [];
        const notAssigned = allCourses.filter(c => !assignedIds.has(c.COURSE_ID));
        this.availableCourses.set(notAssigned);
        // Default to no selection; user must choose explicitly
        this.selectedCourseId.set('');
        this.assignLoading.set(false);
      },
      error: (err) => {
        this.setAssignError(this.describeError(err));
        this.assignLoading.set(false);
      }
    });
  }

  closeAssignDialog(): void {
    this.assignOpen.set(false);
    this.assignMember.set(null);
    this.availableCourses.set([]);
    this.selectedCourseId.set('');
    this.assignError.set('');
    this.assignLoading.set(false);
  }

  onSelectAssignCourse(courseId: string): void {
    this.selectedCourseId.set(courseId || '');
  }

  confirmAssign(): void {
    const member = this.assignMember();
    const courseId = this.selectedCourseId();
    if (!member || !courseId) return;
    this.assignLoading.set(true);
    this.assignError.set('');

    this.courses.assignCourse(member, courseId).subscribe({
      next: () => {
        // Refresh assigned map for this member
        this.employees.getAssignedFor(member).subscribe({
          next: (resp: AssignedCoursesResponse) => {
            const body: any[] = Array.isArray((resp as any)?.body) ? (resp as any).body : [];
            let courses: AssignedCourseItem[] = [];
            if (body.length > 0 && Array.isArray((body[0] as any)?.COURSES)) {
              courses = body.flatMap((it: any) => Array.isArray(it?.COURSES) ? it.COURSES : []) as AssignedCourseItem[];
            } else {
              courses = body
                .filter((row: any) => row && row.COURSE_ID && row.STATUS)
                .map((row: any) => ({
                  COURSE_ID: String(row.COURSE_ID),
                  STATUS: String(row.STATUS).toUpperCase() as AssignedCourseItem['STATUS'],
                  ASSIGNED_ON: row.ASSIGNED_ON ?? undefined,
                  STARTED_ON: row.STARTED_ON ?? undefined,
                  COMPLETED_ON: row.COMPLETED_ON ?? undefined
                }));
            }
            const updated = new Map(this.assignedMap());
            updated.set(member, { loading: false, courses });
            this.assignedMap.set(updated);

            // Also prefetch any new course names
            this.prefetchCourseNames(courses);

            this.assignLoading.set(false);
            this.closeAssignDialog();
          },
          error: (err) => {
            this.setAssignError(this.describeError(err));
            this.assignLoading.set(false);
          }
        });
      },
      error: (err) => {
        this.setAssignError(this.describeError(err));
        this.assignLoading.set(false);
      }
    });
  }

  private describeError(err: any): string {
    if (!err) return 'Request failed.';
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    if (err?.error?.message) return err.error.message;
    return 'Request failed.';
  }

  dismissError() { this.clearError(); }
  private clearError() {
    this.errorText.set('');
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
      this.errorTimer = null;
    }
  }
  private setError(message: string) {
    this.errorText.set(message);
    if (this.errorTimer) clearTimeout(this.errorTimer);
    this.errorTimer = setTimeout(() => this.clearError(), 10000);
  }

  dismissAssignError() { this.clearAssignError(); }
  private clearAssignError() {
    this.assignError.set('');
    if (this.assignErrorTimer) {
      clearTimeout(this.assignErrorTimer);
      this.assignErrorTimer = null;
    }
  }
  private setAssignError(message: string) {
    this.assignError.set(message);
    if (this.assignErrorTimer) clearTimeout(this.assignErrorTimer);
    this.assignErrorTimer = setTimeout(() => this.clearAssignError(), 10000);
  }

  // Bulk assign methods
  openBulkAssign(): void {
    // Prepare members list from all visible employees across all teams
    const members: Array<{id: string, name: string}> = [];
    for (const team of this.teams()) {
      for (const member of team.members) {
        if (member.EMAIL) {
          members.push({
            id: member.EMAIL,
            name: member.NAME || member.EMAIL
          });
        }
      }
    }
    
    this.bulkAssignMembers.set(members);
    this.bulkAssignOpen.set(true);
    
    // Set loading state before API call
    this.bulkAssignCoursesLoading.set(true);
    
    // Load all courses
    this.courses.listCourses().subscribe({
      next: (res) => {
        this.bulkAssignCourses.set(Array.isArray(res?.body) ? res.body : []);
        this.bulkAssignCoursesLoading.set(false);
      },
      error: (err) => {
        this.setError(this.describeError(err));
        this.bulkAssignCoursesLoading.set(false);
      }
    });
  }

  closeBulkAssign(): void {
    this.bulkAssignOpen.set(false);
    this.bulkAssignMembers.set([]);
    this.bulkAssignCourses.set([]);
    this.bulkAssignCoursesLoading.set(false);
  }

  onBulkAssignCompleted(): void {
    // Refresh assigned courses cache for all affected members
    // DO NOT call closeBulkAssign() here - let user manually close after reviewing results
    // Clear assigned cache to force refresh on next expand
    this.assignedMap.set(new Map());
  }
}


